import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FeatureFlagsContext = createContext(null);

const REFRESH_MS = 60_000;

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || user === false) {
      setFlags({});
      setLoaded(true);
      return {};
    }
    setLoading(true);
    try {
      const { data } = await api.get("/feature-flags/me");
      const next = data?.flags || {};
      setFlags(next);
      setLoaded(true);
      return next;
    } catch {
      // Failure non deve bloccare: marca comunque loaded per evitare deadlock rendering
      setLoaded(true);
      return flags;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Al login/logout: fetch + start polling
  useEffect(() => {
    setLoaded(false);
    refresh();
    if (timerRef.current) clearInterval(timerRef.current);
    if (user && user !== false) {
      timerRef.current = setInterval(() => { refresh(); }, REFRESH_MS);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user, refresh]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, loaded, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error("useFeatureFlags must be inside FeatureFlagsProvider");
  return ctx;
}
